import React, { useState, useRef, useEffect, useCallback } from 'react'
import './RetroKnob.css'

interface RetroKnobProps {
  onChange?: (value: number) => void
  initialValue?: number
  label?: string
  min?: number
  max?: number
  resetValue?: number
  snapTo?: number | null
  labelStep?: number
  suffix?: string
  labelUpright?: boolean
}

const RetroKnob: React.FC<RetroKnobProps> = ({
  onChange,
  initialValue,
  label = 'SIGNAL BOOSTER',
  min = -50,
  max = 50,
  resetValue = 0,
  snapTo = 0,
  labelStep = 10,
  suffix = '',
  labelUpright = true,
}) => {
  const MIN_ANGLE = -140
  const MAX_ANGLE = 140
  const RANGE = max - min
  const START_VALUE = initialValue !== undefined ? initialValue : resetValue
  const SNAP_THRESHOLD = 2

  // Convert angle to value (20-80)
  const angleToValue = (angleDeg: number): number => {
    const normalizedAngle = (angleDeg - MIN_ANGLE) / (MAX_ANGLE - MIN_ANGLE)
    return min + normalizedAngle * RANGE
  }

  // Convert value to angle
  const valueToAngle = (value: number): number => {
    const normalizedValue = (value - min) / RANGE
    return MIN_ANGLE + normalizedValue * (MAX_ANGLE - MIN_ANGLE)
  }

  const [angle, setAngle] = useState(() => valueToAngle(START_VALUE))
  const [isDragging, setIsDragging] = useState(false)
  const knobRef = useRef<HTMLDivElement>(null)

  // Update angle when initialValue changes
  useEffect(() => {
    if (initialValue !== undefined) {
      const newAngle = valueToAngle(initialValue)
      setAngle(newAngle)
    }
  }, [initialValue, min, max])

  const generateScaleMarks = () => {
    const marks = []
    const totalSteps = RANGE / labelStep
    const angleStep = (MAX_ANGLE - MIN_ANGLE) / totalSteps

    for (let i = 0; i <= totalSteps; i++) {
      const value = min + i * labelStep
      // Don't render negative labels to avoid clutter, or maybe just render every 20?
      // Let's render every 20 for cleaner look or keep 10 if it fits
      const markAngle = MIN_ANGLE + i * angleStep
      const distance = Math.abs(angle - markAngle)
      const isActive = distance < 15
      const opacity = isActive ? 1 - distance / 20 : 0.4

      marks.push(
        <div
          key={i}
          className={`scale-mark ${isActive ? 'active' : ''}`}
          style={{
            transform: `rotate(${markAngle}deg)`,
            opacity,
          }}
        >
          <span
            className="scale-label"
            style={
              labelUpright
                ? { transform: `rotate(${-markAngle}deg)` }
                : undefined
            }
          >
            {value}
            {suffix}
          </span>
          <div className="scale-tick"></div>
        </div>
      )
    }
    return marks
  }

  const handleDrag = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !knobRef.current) return
      e.preventDefault()

      const rect = knobRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

      const deltaX = clientX - centerX
      const deltaY = clientY - centerY

      let angleDeg = Math.atan2(deltaY, deltaX) * (180 / Math.PI)
      angleDeg += 90

      if (angleDeg > 180) angleDeg -= 360
      if (angleDeg < MIN_ANGLE) angleDeg = MIN_ANGLE
      if (angleDeg > MAX_ANGLE) angleDeg = MAX_ANGLE

      // Convert angle to value
      let currentValue = angleToValue(angleDeg)

      if (snapTo !== null && Math.abs(currentValue - snapTo) <= SNAP_THRESHOLD) {
        currentValue = snapTo
        angleDeg = valueToAngle(snapTo)
      }

      setAngle(angleDeg)

      // Notify parent
      if (onChange) {
        onChange(currentValue)
      }
    },
    [isDragging, onChange, min, max, snapTo]
  )

  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true)
    e.preventDefault()
  }

  const stopDrag = () => {
    setIsDragging(false)
  }

  const handleDoubleClick = () => {
    const resetAngle = valueToAngle(resetValue)
    setAngle(resetAngle)
    if (onChange) {
      onChange(resetValue)
    }
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDrag)
      document.addEventListener('mouseup', stopDrag)
      document.addEventListener('touchmove', handleDrag, { passive: false })
      document.addEventListener('touchend', stopDrag)
      document.body.style.cursor = 'grabbing'

      return () => {
        document.removeEventListener('mousemove', handleDrag)
        document.removeEventListener('mouseup', stopDrag)
        document.removeEventListener('touchmove', handleDrag)
        document.removeEventListener('touchend', stopDrag)
        document.body.style.cursor = ''
      }
    }
  }, [isDragging, handleDrag])

  return (
    <div className="knob-wrapper">
      <span className="control-label">{label}</span>

      <div className="knob-container">
        <div className="knob-scale">{generateScaleMarks()}</div>

        <div className="knob-casing">
          <div
            ref={knobRef}
            className="knob-rotator"
            style={{ transform: `rotate(${angle}deg)` }}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            onDoubleClick={handleDoubleClick}
          >
            <div className="knob-grip"></div>
            <div className="knob-face"></div>
            <div className="knob-indicator"></div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RetroKnob
